import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Typography, TextField, Alert, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PageContainer, DataTableContainer } from '../../../components';
import {
  TABLE_COLUMNS,
  FILTER_CONFIGS,
  SEARCH_CONFIGS,
} from '../../../constants/tableConfigurations';
import {
  useAuth,
  useUserRevenueEntries,
  useFirms,
  usePricingMethods,
  useBonusRevenueTypes,
  useNotification,
  useUsers,
  useRevenueCalculationRules,
} from '../../../hooks';
import { hasuraClient } from '../../../utils/graphqlClient';
import {
  GET_REVENUE_CALCULATION_RULE_MATCH_QUERY,
  GET_REVENUE_CALCULATION_RULE_MATCH_FOR_SALES_QUERY,
  GET_LATEST_PAST_OVERRIDE,
} from '../../../graphql/queries';
import {
  INSERT_PAST_OVERRIDE,
  DELETE_PAST_OVERRIDES_FOR_JOB_CHANNEL,
} from '../../../graphql/mutations';
import { computeQuarterAmount, REVENUE_CALC_REASONS, pickProjectPeriod } from '../../../utils/revenueCalculation';
import {
  evaluateBlockingWarnings,
  BLOCKING_WARNING_KEYS,
  entryClosure,
  closureWarningKey,
  warningKeyText,
} from '../../../utils/revenueWarnings';
import { enrichRevenueEntries, buildRuleLookupKey } from '../../../utils/revenueEntryEnrichment';
import { buildRevenueFormFields } from '../../../utils/revenueFormFields';
import { resolveFillMode, findSelectedJob } from '../../../utils/revenueFillMode';
import CiroTalepDetailModal from '../../ApplicationsPage/CiroTalepleriPage/CiroTalepDetailModal';

// Tutari AYLIK girilmesi gereken ciro turleri (kod). Bkz. renderFieldExtras.
const AYLIK_TUTAR_KODLARI = new Set(['CT2', 'CT3']);

// Paket 2: submit'i engelleyen 4 kirmizi uyari icin toast metinleri.
// Anahtar tr.json/en.json'a "userRevenueEntries.blocking.<key>" altinda kayitli;
// defaultValue burada fallback olarak veriliyor (i18n yuklenmemis edge case).
// Sure hesap formulunde kullanilan renkli mini kart. Chip yerine kullaniliyor
// (chip'in click event'i drag/modal scope'unda garip bubbling'e sebep oluyordu).
// editable prop verilirse ustune tiklaninca kucuk edit ikonu + Enter/blur ile
// save. onEditSave(newNumericValue) callback ile parent'a bildirir.
const FormulaBadge = ({
  label, value, color = 'primary', bold = false,
  editable = false, editValue, onEditSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(editValue ?? 0);

  useEffect(() => { setDraft(editValue ?? 0); }, [editValue]);

  const commit = () => {
    const n = Number(draft);
    if (Number.isFinite(n) && n >= 0) onEditSave?.(n);
    setEditing(false);
  };

  return (
    <Box
      onClick={editable && !editing ? () => setEditing(true) : undefined}
      sx={{
        position: 'relative',
        minWidth: 82,
        px: 1.25, py: 0.75, borderRadius: 1.5,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette[color].main, editable ? 0.55 : 0.35),
        bgcolor: (theme) => alpha(theme.palette[color].main, bold ? 0.14 : 0.06),
        textAlign: 'center',
        cursor: editable && !editing ? 'pointer' : 'default',
        transition: 'border-color 0.12s, box-shadow 0.12s',
        ...(editable && !editing && {
          '&:hover': {
            borderColor: (theme) => alpha(theme.palette[color].main, 0.8),
            boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette[color].main, 0.12)}`,
          },
        }),
      }}
    >
      <Typography
        sx={{
          fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
          textTransform: 'uppercase', color: `${color}.dark`,
          lineHeight: 1.2, mb: 0.15,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.35,
        }}
        noWrap
      >
        {label}
        {editable && !editing && (
          <EditOutlinedIcon sx={{ fontSize: 10, opacity: 0.7 }} />
        )}
      </Typography>
      {editing ? (
        <TextField
          autoFocus
          type="number"
          size="small"
          variant="standard"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          onClick={(e) => e.stopPropagation()}
          slotProps={{
            htmlInput: {
              min: 0,
              style: {
                textAlign: 'center',
                fontSize: bold ? 15 : 13,
                fontWeight: bold ? 800 : 700,
                padding: '2px 4px',
                width: 44,
              },
            },
          }}
          sx={{ '& .MuiInput-underline:before': { borderBottomColor: `${color}.main` } }}
        />
      ) : (
        <Typography
          sx={{
            fontSize: bold ? 15 : 13, fontWeight: bold ? 800 : 700,
            color: `${color}.dark`, lineHeight: 1.2,
          }}
          noWrap
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};

const BLOCKING_DEFAULTS = {
  [BLOCKING_WARNING_KEYS.unapproved_job]: 'Onaylı olmayan projeye onay sonrası prim giremezsiniz.',
  [BLOCKING_WARNING_KEYS.rejected_job]: 'Reddedilen işe onay sonrası ciro giremezsiniz.',
  [BLOCKING_WARNING_KEYS.no_project_dates]: 'Proje tarihleri güncellenmeyen işe prim giremezsiniz.',
  [BLOCKING_WARNING_KEYS.period_over]: 'Projenin prim dönemi bitmiştir.',
  [BLOCKING_WARNING_KEYS.no_firm_contact]: 'İletişim kartı boş olan firmaya ciro giremezsiniz.',
  [BLOCKING_WARNING_KEYS.duplicate_quarter_entry]: 'Daha önce bu işe bu süreç için ciro girilmiş. Tekrar girmek istiyorsanız önceki ciroyu siliniz.',
  [BLOCKING_WARNING_KEYS.no_job_status]: 'İş durumu boş olan işe yazım cirosu giremezsiniz. Önce iş durumunu güncelleyin.',
};

const CiroEklePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const isRootUser = user?.role === 'ROOT';
  const isTeamManager = user?.role === 'TEAM_MANAGER';
  const targetUserId = user?.id;

  // ROOT baskasinin adina ciro girebilir; kendi companyId'sinde tum
  // kullanicilari drop-down'a listeleriz. Non-ROOT icin fetch hic
  // tetiklenmez (formda alan da gorunmez).
  const { users: allUsers, fetchData: fetchUsers } = useUsers();

  const {
    userRevenueEntries,
    isLoading,
    handleFormSubmit,
    fetchData,
    deleteUserRevenueEntries,
  } = useUserRevenueEntries({
    // ROOT baskasi adina ciro girebildigi icin listeyi kendi user_id'sine
    // kisitlamayalim — kendi kayitlari + baska kullanicilar adina girdigi
    // kayitlar hepsi gorunsun. Hook `userId=null` iken ROOT icin sirket
    // capinda tum kayitlari (where:{}) dondurur. Non-ROOT'ta kendi id'si
    // gecerli filtre.
    userId: isRootUser ? null : targetUserId,
    paginated: false,
  });

  // useFirms zaten contracts + jobs'u nested olarak döndürüyor (firms query'sinde).
  // InvoiceManagementPage'deki ile aynı pattern: firma seçince selectedFirm.contracts
  // ve selectedFirm.jobs üzerinden dependent dropdown.
  // bypassRestriction: kullanici yetkili olmadigi firmalara da ciro
  // (özellikle satış cirosu) girebilmeli. Backend permission zaten
  // company_id ile kısıtladığından güvenli.
  const { firms, fetchData: fetchFirms } = useFirms({ bypassRestriction: true });

  // Kural eşleşmesi için Ciro Kanalı (bonus_revenue_types) referansı —
  // user_revenue_entries.ciro_kanali enum (SATIS_CIROSU/PROJE_YAZIM/ONAY_SONRASI)
  // ile bonus_revenue_types.code üzerinden eşleşiyor. pricingMethods
  // ön-yükleme için çağırılıyor; rule lookup sadece pricing_method_id ile
  // çalıştığı için listenin kendisine ihtiyaç yok.
  const { fetchData: fetchPricingMethods } = usePricingMethods();
  // Ciro kanalı dropdown'u Ayarlar > Ciro Türleri (bonus_revenue_types)'nden
  // beslenir. Label = bonus_revenue_types.name; value, name'den substring
  // ile çıkarılan ciro_kanali enum'u (DB CHECK constraint nedeniyle).
  const { bonusRevenueTypes, fetchData: fetchBonusRevenueTypes } = useBonusRevenueTypes();

  // PENDING dışındaki kayıtlarda "Görüntüle" aksiyonu → detay modal.
  // CiroTalepleriPage ile aynı modal'ı kullanıyoruz (reject_reason'ı da
  // gösteriyor — kullanıcı tooltip dışında düz okuma için açabilir).
  const [detailEntry, setDetailEntry] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Otomatik Doldur butonu computeQuarterAmount'tan dönen ay sayısını ve
  // güncel userRevenueEntries listesini REF'te tutuyoruz. State'e koymak modal
  // açıkken re-render tetikleyip form'un mount'unu sıfırlıyordu — input'lar
  // boşalıyordu. Ref güncellemesi render tetiklemez, formFields useMemo'sunun
  // dep listesinden de çıkardık (aşağıda).
  const lastFetchMonthsRef = useRef({ key: null, months: null });
  const userRevenueEntriesRef = useRef([]);
  useEffect(() => {
    userRevenueEntriesRef.current = userRevenueEntries || [];
  }, [userRevenueEntries]);

  // ROOT'un revize ettigi "Gecmiste Girilen Sure" — persistent history.
  // Her iş+kanal icin en son override (created_at DESC LIMIT 1). Formula
  // ve tabloda past hesabinda kullanilir: past = override.months + (o
  // override'dan SONRA girilen kayitlarin ay_sayisi toplami).
  const [pastOverrides, setPastOverrides] = useState({});
  const pastOverridesRef = useRef({});
  useEffect(() => { pastOverridesRef.current = pastOverrides; }, [pastOverrides]);
  useEffect(() => {
    const list = userRevenueEntries || [];
    if (list.length === 0) return;
    const combos = new Map();
    list.forEach((e) => {
      if (!e.job_id || !e.ciro_kanali) return;
      combos.set(`${e.job_id}|${e.ciro_kanali}`, { jobId: e.job_id, ciroKanali: e.ciro_kanali });
    });
    let cancelled = false;
    (async () => {
      const acc = {};
      for (const [key, { jobId, ciroKanali }] of combos) {
        try {
          const data = await hasuraClient.query(GET_LATEST_PAST_OVERRIDE, { jobId, ciroKanali });
          const latest = data?.revenue_past_overrides?.[0];
          if (latest) acc[key] = latest;
        } catch (_e) { /* override yoksa sorun degil */ }
      }
      if (!cancelled) setPastOverrides(acc);
    })();
    return () => { cancelled = true; };
  }, [userRevenueEntries]);

  const insertPastOverride = useCallback(async ({ jobId, ciroKanali, overrideMonths, notes }) => {
    if (!user?.companyId) return null;
    try {
      const res = await hasuraClient.mutate(INSERT_PAST_OVERRIDE, {
        jobId, ciroKanali, overrideMonths, notes: notes || null,
        companyId: user.companyId, createdBy: user.id,
      });
      const created = res?.insert_revenue_past_overrides_one;
      if (created) {
        setPastOverrides((prev) => ({ ...prev, [`${jobId}|${ciroKanali}`]: created }));
      }
      return created;
    } catch (err) {
      showNotification(
        t('userRevenueEntries.pastOverride.error', {
          defaultValue: 'Geçmiş revize kaydedilemedi',
        }),
        'error',
      );
      return null;
    }
  }, [user?.companyId, user?.id, showNotification, t]);

  const rowActions = useCallback((row) => {
    // TEAM_MANAGER + baskasinin (ekip uyesi) kaydi → sadece Goruntule.
    // Kendi kaydinda ise mevcut Non-ROOT davranisi (PENDING'de edit/delete,
    // APPROVED/REJECTED'da sadece review) devrede.
    const isOwnRow = !row?.user_id || parseInt(row.user_id) === parseInt(user?.id);
    if (isTeamManager && !isOwnRow) {
      return [
        {
          type: 'review',
          label: 'userRevenueEntries.actions.review',
          color: 'info',
          icon: <SearchIcon fontSize="small" />,
        },
      ];
    }
    // APPROVED/REJECTED — non-ROOT icin kilit (yalnizca Goruntule). ROOT bu
    // durumda da direkt edit/delete yapabilir (ekstra denetim yok — bu sayfa
    // zaten ROOT icin genis yetkili). Non-ROOT icin edit ROOT'un ayrica
    // "Ciro Onay" sayfasindan aciktir; burada goruntuleyip talep acabilir.
    if (row?.status === 'APPROVED' || row?.status === 'REJECTED') {
      if (isRootUser) {
        return [
          {
            type: 'edit',
            label: 'userRevenueEntries.actions.edit',
            color: 'primary',
            icon: <EditIcon fontSize="small" />,
          },
          {
            type: 'delete',
            label: 'userRevenueEntries.actions.delete',
            color: 'error',
            icon: <DeleteIcon fontSize="small" />,
          },
          {
            type: 'review',
            label: 'userRevenueEntries.actions.review',
            color: 'info',
            icon: <SearchIcon fontSize="small" />,
          },
        ];
      }
      return [
        {
          type: 'review',
          label: 'userRevenueEntries.actions.review',
          color: 'info',
          icon: <SearchIcon fontSize="small" />,
        },
      ];
    }
    // PENDING — kullanici (rol fark etmez) kendi kaydini duzenleyebilir/silebilir.
    return [
      {
        type: 'edit',
        label: 'userRevenueEntries.actions.edit',
        color: 'primary',
        icon: <EditIcon fontSize="small" />,
      },
      {
        type: 'delete',
        label: 'userRevenueEntries.actions.delete',
        color: 'error',
        icon: <DeleteIcon fontSize="small" />,
      },
    ];
  }, [isRootUser, isTeamManager, user?.id]);

  // 'review' page-level state'e gider; diğer aksiyonlar (edit/delete)
  // DataTableContainer'in default davranışına bırakılır (return false).
  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'review' && row) {
      setDetailEntry(row);
      setDetailModalOpen(true);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (targetUserId) {
      fetchData();
      fetchFirms();
      fetchPricingMethods();
      fetchBonusRevenueTypes();
      if (isRootUser) {
        fetchUsers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId, isRootUser]);

  // bonus_revenue_type_id → coefficient + name lookup'lari. enrichRevenueEntries
  // hem hedefe esas ciro hesabini hem Ciro Turu kolonu icin satirlara
  // `bonusRevenueTypeName` ekler.
  const coefficientById = useMemo(() => {
    const map = new Map();
    (bonusRevenueTypes || []).forEach((b) => {
      const c = Number(b?.coefficient);
      map.set(b.id, Number.isFinite(c) ? c : 1);
    });
    return map;
  }, [bonusRevenueTypes]);

  const typeNameById = useMemo(() => {
    const map = new Map();
    (bonusRevenueTypes || []).forEach((b) => {
      map.set(b.id, b.name || `#${b.id}`);
    });
    return map;
  }, [bonusRevenueTypes]);

  // ONAY_SONRASI Kac/Kac (x/y) kolonu icin rule lookup — CiroTalepleri
  // ile ayni pattern. Rule tablosu her pricing_method_id icin ayri key.
  const { revenueCalculationRules: revenueRules } = useRevenueCalculationRules();
  const rulesByKey = useMemo(() => {
    const map = new Map();
    (revenueRules || []).forEach((rule) => {
      if (!rule || rule.status === 'inactive') return;
      const ids = Array.isArray(rule.pricing_method_ids) ? rule.pricing_method_ids : [];
      const jobTypeId = rule.job_type_id ?? null;
      const channel = rule.revenue_channel;
      if (ids.length === 0) {
        map.set(buildRuleLookupKey(jobTypeId, channel, null), rule);
      } else {
        ids.forEach((pmId) => {
          map.set(buildRuleLookupKey(jobTypeId, channel, pmId), rule);
        });
      }
    });
    return map;
  }, [revenueRules]);

  // Tabloda 6 yesil/sari uyari kolonunun ("Uyarilar") gosterilmesi icin
  // satirlara warningsKeys ekliyoruz. Onayci ekrani CiroTalepleri ile ayni
  // helper.
  const enrichedData = useMemo(
    () =>
      enrichRevenueEntries(userRevenueEntries, {
        coefficientById,
        typeNameById,
        currentUserTeamId: user?.team_id ?? null,
        rulesByKey,
        pastOverrides,
      }),
    [userRevenueEntries, coefficientById, typeNameById, user?.team_id, rulesByKey, pastOverrides],
  );

  // Form fields — firm_id / bonus_revenue_type_id / contract_id / job_id
  // option injection'i ortak helper'dan, ardindan bu sayfaya ozgu "Tutar Getir"
  // (numberWithButton) override'i tutar alanina uygulaniyor. companyId alani
  // helper icinde filtrelenir.
  const formFields = useMemo(() => {
    const base = buildRevenueFormFields({ firms, bonusRevenueTypes });
    const firmsList = firms || [];

    // ONAY_SONRASI icin ceyrek/secili_aylar otomatik on-doldurma helper'lari.
    // Onaylanma tarihi oncelikle job_details[i].detail_data.approvalDate'ten,
    // yoksa job.job_status === 'ONAYLANDI' iken job.updated_at'ten alinir.
    const findJobFromFormData = (fd) => {
      const firm = firmsList.find(
        (fm) => fm.id === parseInt(fd?.firm_id) || fm.id === fd?.firm_id,
      );
      if (!firm) return null;
      return (firm.jobs || []).find((j) => j.id === fd?.job_id) || null;
    };
    const deriveApprovalDate = (job) => {
      if (!job) return null;
      if (Array.isArray(job.job_details)) {
        for (const d of job.job_details) {
          if (d?.detail_data?.approvalDate) return d.detail_data.approvalDate;
        }
      }
      if (job.job_status === 'ONAYLANDI' && job.updated_at) return job.updated_at;
      return null;
    };
    // Ceyregin, onay tarihinden SONRAKI aylari.
    //
    // YIL KARSILASTIRMASI SART: onceki surum yalnizca ceyrek numarasina
    // bakiyordu. Onayi 04.08.2025 olan bir iste 2026/Q3 formu acildiginda
    // "ceyrek === onay ceyregi" dogru ciktigi icin Agustos kesme noktasi
    // 2026'ya da uygulaniyor ve Temmuz 2026 dusuyordu. Onay yilindan
    // sonraki yillarda ceyregin TUM aylari uygundur.
    const monthsOfQuarterOnOrAfter = (quarter, approvalMonth, approvalQuarter, yil, approvalYear) => {
      if (!Number.isFinite(quarter)) return [];
      const start = (quarter - 1) * 3 + 1;
      const end = quarter * 3;
      const out = [];
      const yilBiliniyor = Number.isFinite(yil) && Number.isFinite(approvalYear);
      for (let m = start; m <= end; m++) {
        if (yilBiliniyor && yil > approvalYear) out.push(m);        // sonraki yil: tum aylar
        else if (yilBiliniyor && yil < approvalYear) continue;       // onay oncesi yil: hicbiri
        else if (quarter > approvalQuarter) out.push(m);             // ayni yil, sonraki ceyrek
        else if (quarter === approvalQuarter && m >= approvalMonth) out.push(m);
      }
      return out;
    };
    // Bu is + kanal icin KALAN ay hakki: toplam sure - girilmis sure.
    // Otomatik doldur bunu bilmeden ceyregin tum aylarini seciyordu; 11/12
    // girilmis bir iste Q3 icin Agustos+Eylul (2 ay) getirip 13/12 gibi
    // imkansiz bir toplam uretiyordu. Artik secim kalan hakla sinirli.
    //
    // Ilk kayitta (hic gecmis yoksa) toplam sure bilinmedigi icin kirpma
    // yapilmaz — kural/compute zaten dogru y'yi getiriyor.
    const kalanAyHakki = (fd) => {
      const jobId = fd?.job_id;
      const kanal = fd?.ciro_kanali;
      if (jobId == null || !kanal) return null;
      const kayitlar = (userRevenueEntries || []).filter((e) => {
        if (e?.status === 'REJECTED') return false;
        if (e?.job_id !== jobId) return false;
        if (e?.ciro_kanali !== kanal) return false;
        if (fd?.id != null && e?.id === fd.id) return false;
        return true;
      });
      if (kayitlar.length === 0) return null;
      const toplamSure = Math.max(...kayitlar.map((e) => Number(e.toplam_ay) || 0));
      if (!toplamSure) return null;
      const girilmis = kayitlar.reduce((acc, e) => acc + (Number(e.ay_sayisi) || 0), 0);
      return Math.max(0, toplamSure - girilmis);
    };

    const computePrefill = (fd, quarterOverride = null) => {
      if (fd?.ciro_kanali !== 'ONAY_SONRASI_CIROSU') return null;
      const job = findJobFromFormData(fd);
      const approvalDate = deriveApprovalDate(job);
      if (!approvalDate) return null;
      const d = new Date(approvalDate);
      if (Number.isNaN(d.getTime())) return null;
      const approvalMonth = d.getMonth() + 1;
      const approvalQuarter = Math.ceil(approvalMonth / 3);
      const approvalYear = d.getFullYear();
      const quarter = quarterOverride
        ? parseInt(quarterOverride, 10)
        : approvalQuarter;
      // Formun yili: secilmemisse onay yili varsayilir.
      const yil = Number.isFinite(Number(fd?.yil)) && Number(fd?.yil) > 0
        ? Number(fd.yil)
        : approvalYear;
      const tumAylar = monthsOfQuarterOnOrAfter(
        quarter, approvalMonth, approvalQuarter, yil, approvalYear,
      );
      // Kalan hak kadar ay secilir; en erken aylar oncelikli.
      const kalan = kalanAyHakki(fd);
      const months = kalan == null ? tumAylar : tumAylar.slice(0, kalan);
      return { ceyrek: quarter, secili_aylar: months };
    };

    const withSideEffects = base.map((f) => {
      if (f.name === 'ciro_kanali') {
        return {
          ...f,
          onChangeSideEffects: (value, formData) => {
            // Kullanicinin sectigi ceyrek varsa koru, override etme.
            // Sadece formData.ceyrek bossa approval'a gore otomatik dolsun.
            const keepQuarter = formData.ceyrek || null;
            return computePrefill(
              { ...formData, ciro_kanali: value },
              keepQuarter,
            );
          },
        };
      }
      if (f.name === 'job_id') {
        return {
          ...f,
          onChangeSideEffects: (value, formData) => {
            const keepQuarter = formData.ceyrek || null;
            return computePrefill(
              { ...formData, job_id: value },
              keepQuarter,
            );
          },
        };
      }
      if (f.name === 'ceyrek') {
        return {
          ...f,
          onChangeSideEffects: (value, formData) => {
            const prefill = computePrefill(
              { ...formData, ceyrek: value },
              value,
            );
            // Ceyrek degistiginde ceyregi override etmeyelim, sadece
            // secili_aylar'i yeni ceyregin uygun aylarina yenile.
            return prefill ? { secili_aylar: prefill.secili_aylar } : null;
          },
        };
      }
      return f;
    });

    const withTutar = withSideEffects.map((f) => {
      if (f.name !== 'tutar') return f;
      // "Tutar Getir" butonu — kullanıcı form'daki firma/iş/sözleşme/kanal
      // + yıl/çeyrek seçtikten sonra tıklar; sistem kuralı arar, bu çeyreğe
      // isabet eden tutarı + x/y göstergesini döndürür. Tutar manuel
      // düzenlenebilir kalır. Hesap kuralı: kullanıcı spec'ine göre
      // (computeQuarterAmount içinde dokümante).
      return {
        ...f,
        type: 'numberWithButton',
        fetchButtonLabel: 'userRevenueEntries.fetchAmount',
        // Buton her zaman gizli — Otomatik modda autoFetch zaten
        // tetikleniyor (doldurma modu secilince direkt calisir), Manuel
        // modda kullanici tutari elle yaziyor.
        hideButton: () => true,
        // NumberWithButtonField'in opt-in mekanizmasi: Otomatik Doldur
        // basildiginda false, kullanici elle yazinca true set edilir.
        // Warning evaluator (revenueWarnings.js) bu flag'e bakip
        // "Tutar Manuel" chip'ini gosterir. Default degeri hook'ta
        // transformFormData icinde true'ya kilitli — override sadece
        // Otomatik Doldur ile mumkun.
        manualFlagField: 'is_manual_amount',
        // Otomatik Doldur mode + gerekli alanlar seciliyken kullaniciya
        // butona basmadan otomatik tetikle. Key her degistiginde bir kez
        // calisir; null donunce reset olur.
        // ROOT "Gecmis"i manuel revize etmisse (_past_override) autoFetch
        // TAMAMEN devre disi kalir — hicbir alan kullanicinin kontrolunden
        // cikmaz.
        //
        // NumberWithButtonField useEffect'i formData disi kaynaklardaki
        // degisiklikleri de yakalasin diye extra dep fonksiyonu. Return
        // degeri (string) degisince useEffect tetiklenir; boylece
        // userRevenueEntries async gelince autoFetch dogru past ile
        // yeniden calisir.
        autoFetchExtraDep: (data) => {
          const entries = userRevenueEntriesRef.current || [];
          const relevant = entries.filter(
            (e) => e.job_id === data?.job_id
              && e.ciro_kanali === data?.ciro_kanali
              && e.status !== 'REJECTED',
          );
          const pastSum = relevant.reduce((acc, e) => acc + (Number(e.ay_sayisi) || 0), 0);
          const overrideKey = `${data?.job_id}|${data?.ciro_kanali}`;
          const persisted = pastOverridesRef.current?.[overrideKey];
          const overrideStamp = persisted
            ? `${persisted.override_months}@${persisted.created_at}`
            : 'none';
          return `${relevant.length}:${pastSum}|${overrideStamp}`;
        },
        autoFetchKey: (data) => {
          if (data?._past_override != null) return null;
          if (data?.ciro_kanali !== 'ONAY_SONRASI_CIROSU') return null;
          // MOD KONTROLU BURADA YOK — bilerek.
          // Payda (y = toplam_ay) yalnizca onFetch icinde hesaplaniyor.
          // Burada manuel modda `null` donunce fetch hic calismiyor, y
          // bos kaliyor ve tabloda "12/13" uretilemiyor (xyLabel hem x
          // hem y ister). Modun isi TUTAR'i otomatik doldurup
          // doldurmamak; onu onFetch sonunda ayarliyoruz.
          if (!data?.job_id || !data?.yil || !data?.ceyrek) return null;
          const months = Array.isArray(data.secili_aylar) ? data.secili_aylar.join(',') : '';
          // Bu iş+kanal icin past imza + persistent override stamp'i:
          // async fetch'ler tamamlaninca key degisir ve autoFetch dogru
          // degerlerle tekrar calisir.
          const entries = userRevenueEntriesRef.current || [];
          const relevant = entries.filter(
            (e) => e.job_id === data.job_id
              && e.ciro_kanali === data.ciro_kanali
              && e.status !== 'REJECTED',
          );
          const pastSum = relevant.reduce((acc, e) => acc + (Number(e.ay_sayisi) || 0), 0);
          const overrideKey = `${data.job_id}|${data.ciro_kanali}`;
          const persisted = pastOverridesRef.current?.[overrideKey];
          const overrideStamp = persisted
            ? `${persisted.override_months}@${persisted.created_at}`
            : 'none';
          const mod = resolveFillMode(data, findSelectedJob(firmsList, data));
          // Manuel modda x kullanicidan geliyor: yazdikca toplam kontrolu
          // ve panel tazelenmeli. Otomatik modda x'i sistem dolduruyor;
          // anahtara katmak gereksiz bir ikinci hesap tetiklerdi.
          const xAnahtar = mod === 'manual' ? `|x${data.ay_sayisi ?? ''}` : '';
          return `auto|${mod}${xAnahtar}|${data.firm_id}|${data.job_id}|${data.yil}|${data.ceyrek}|${months}|${relevant.length}:${pastSum}|${overrideStamp}`;
        },
        onFetch: async (formData) => {
          const channelEnum = formData?.ciro_kanali;
          if (!channelEnum) {
            console.warn('[CiroGetir] Ciro Kanalı seçilmedi');
            return { amount: null, helperText: 'Ciro kanalı seçilmedi.', severity: 'warning' };
          }
          const CHANNEL_MAP = {
            SATIS_CIROSU: 'SATIS',
            PROJE_YAZIM_CIROSU: 'YAZIM',
            ONAY_SONRASI_CIROSU: 'ONAY_SONRASI',
          };
          const revenueChannel = CHANNEL_MAP[channelEnum] || null;
          if (!formData?.firm_id) {
            return { amount: null, helperText: 'Firma seçilmedi.', severity: 'warning' };
          }
          if (!formData?.yil || !formData?.ceyrek) {
            return { amount: null, helperText: 'Yıl ve çeyrek seçilmeli.', severity: 'warning' };
          }
          const selectedFirm = firmsList.find(
            (fm) => fm.id === parseInt(formData.firm_id) || fm.id === formData.firm_id,
          );
          let contract = null;
          let job = null;
          if (channelEnum === 'SATIS_CIROSU') {
            if (!formData.contract_id) {
              return { amount: null, helperText: 'Sözleşme seçilmedi.', severity: 'warning' };
            }
            contract = selectedFirm?.contracts?.find(
              (c) => c.id === parseInt(formData.contract_id) || c.id === formData.contract_id,
            );
            if (contract?.id) {
              job = selectedFirm?.jobs?.find((j) => j.contract_id === contract.id) || null;
            }
          } else {
            if (!formData.job_id) {
              return { amount: null, helperText: 'İş seçilmedi.', severity: 'warning' };
            }
            job = selectedFirm?.jobs?.find((j) => j.id === formData.job_id);
            if (job?.contract_id) {
              contract = selectedFirm?.contracts?.find((c) => c.id === job.contract_id);
            }
          }
          const jobTypeId = job?.job_type_id;
          const pricingMethodId = contract?.pricing_method_id;
          const companyId = user?.companyId;

          // Payda (y) proje suresi = monthsBetween(projectStart, projectEnd).
          // Kural / pricing_method / fatura olmasa BILE payda proje
          // tarihlerinden bagimsiz hesaplanabilir. Kullanici istegi: "payda
          // her daim gorunmeli". Erken bailout senaryolarinda extraFields
          // ile forma yaziyoruz — formda "Prim Odemesine Iliskin Sure —
          // Payda (y)" alani bosalsa da bu deger dolar ve panel'e yansir.
          // Tekrar kullanilabilir olsun diye burada bir kere hesapla.
          const projectPeriod = pickProjectPeriod(job, contract);
          const bailoutExtraFields = projectPeriod?.months > 0
            ? { toplam_ay: projectPeriod.months }
            : {};

          if (!pricingMethodId) {
            return {
              amount: null,
              helperText: !contract
                ? 'Bu işe bağlı sözleşme bulunamadı. Önce işi bir sözleşmeye bağlayın.'
                : 'Sözleşmede "Ücretlendirme Şekli" seçili değil. Sözleşmeyi düzenleyip ekleyiniz.',
              severity: 'warning',
              extraFields: bailoutExtraFields,
            };
          }
          if (!companyId) {
            return {
              amount: null,
              helperText: 'Şirket bilgisi yok.',
              severity: 'warning',
              extraFields: bailoutExtraFields,
            };
          }
          if (channelEnum !== 'SATIS_CIROSU' && !jobTypeId) {
            return {
              amount: null,
              helperText: 'İşin job_type_id\'si yok.',
              severity: 'warning',
              extraFields: bailoutExtraFields,
            };
          }
          try {
            const useSalesNullQuery = channelEnum === 'SATIS_CIROSU' && !jobTypeId;
            const query = useSalesNullQuery
              ? GET_REVENUE_CALCULATION_RULE_MATCH_FOR_SALES_QUERY
              : GET_REVENUE_CALCULATION_RULE_MATCH_QUERY;
            const params = useSalesNullQuery
              ? { companyId, revenueChannel, pricingMethodId }
              : { companyId, jobTypeId, revenueChannel, pricingMethodId };
            console.log('[CiroGetir] params:', params);
            const result = await hasuraClient.query(query, params);
            const rule = result?.revenue_calculation_rules?.[0];
            if (!rule) {
              return {
                amount: null,
                helperText: 'Bu kombinasyon için kural tanımlı değil.',
                severity: 'warning',
                extraFields: bailoutExtraFields,
              };
            }
            // Onaylanma tarihi: job_details içinden ONAYLANDI olan satırın
            // detail_data.approvalDate'i (JobStatusUpdateModal kaydeder).
            let approvalDate = null;
            if (Array.isArray(job?.job_details)) {
              for (const d of job.job_details) {
                const dd = d?.detail_data;
                if (dd?.approvalDate) {
                  approvalDate = dd.approvalDate;
                  break;
                }
              }
            }
            // Mod hesaptan ONCE: manuel modda (onay sonrasi TUBITAK hep
            // manuel) x yalnizca kullanicidan gelir.
            const otomatikTutar = resolveFillMode(formData, job) === 'auto';
            const computed = computeQuarterAmount({
              rule,
              contract,
              job,
              approvalDate,
              yil: formData.yil,
              ceyrek: formData.ceyrek,
              existingEntries: userRevenueEntriesRef.current,
              // Edit modda kendi kaydini past hesabina dahil etme (aksi
              // halde 3'lu kayit 3+3=6 gorunur).
              currentEntryId: formData?.id,
              // Kullanicinin sectigi aylar varsa hesap onlara gore.
              selectedMonths: Array.isArray(formData.secili_aylar)
                ? formData.secili_aylar
                : null,
              enteredMonths: formData.ay_sayisi,
              onlyEnteredMonths: !otomatikTutar,
            });
            console.log('[CiroGetir] computed:', computed);

            // ay_sayisi'ı submit zamanında formData'ya basabilmek için
            // state'e koy (kural çekilemezse submit guard'ı bunu kullanır).
            const fetchKey = [
              formData.firm_id,
              formData.job_id || formData.contract_id || '',
              channelEnum,
              formData.yil,
              formData.ceyrek,
            ].join('|');
            if (computed && computed.quarterMonths != null) {
              lastFetchMonthsRef.current = { key: fetchKey, months: computed.quarterMonths };
            }

            // Pay (x): kullanici Ay Sayisi alanini elle degistirdiyse ONA
            // saygi goster. Payda (y): sistem hesabi (kural bazli).
            // NOT: `Number('')` = 0 oldugu icin `Number.isFinite` yeterli
            // degil — bos string / null'i once ayikliyoruz (aksi halde
            // bos alan `0` gibi gorunuyordu → "0/18" yaniltmasi).
            const rawAy = formData?.ay_sayisi;
            const userAySayisiNum = (rawAy != null && rawAy !== '' && Number.isFinite(Number(rawAy)))
              ? Number(rawAy)
              : null;
            const displayX = userAySayisiNum != null ? userAySayisiNum : (computed.x ?? 0);
            const xy = computed.y ? `${displayX}/${computed.y}` : null;
            // TOPLAM SUREYI ASMA KONTROLU.
            //
            // Ibrahim: "Personel oraya 12 de yazabilir, ama senin hata
            // vermen lazim: toplam sureden daha fazla giremezsin diye."
            // Manuel modda elle yazilan ay sayisi denetlenmiyordu; 3 aylik
            // bir doneme 12 yazilinca panel sessizce "12/3" gosteriyordu.
            // Girisi ENGELLEMIYORUZ (Ibrahim: "izin ver") — yazilabiliyor
            // ama panel hata veriyor.
            const sureAsimi = userAySayisiNum != null
              && Number.isFinite(computed.y)
              && computed.y > 0
              && userAySayisiNum > computed.y;
            // Bu is+kanal icin gecmiste girilmis toplam ay (computed.x su ceyrek
            // dahil oldugu icin quarterMonths'u cikaririz). Kullanicinin ilk
            // istegi: "bu isle bu kadar aylik prim girilmiş" info.
            // Hesap gecmisi dogrudan donduruyor. Onceden x - quarterMonths
            // ile geri turetiliyordu; x artik kullanicinin girdigi aydan
            // geldigi icin o formul yanlis sonuc verirdi.
            const pastMonths = Number.isFinite(computed.pastMonths) ? computed.pastMonths : null;
            // MISSING_CONTRACT_AMOUNT: fatura yok, kullanici manuel girecek —
            // xy/tutar bilgisi yanilticidir ("0/18" ya da "0 ₺" gostermek
            // yerine yalniz uyari mesaji goster).
            const isMissingAmount = computed.reason === REVENUE_CALC_REASONS.MISSING_CONTRACT_AMOUNT;
            // Resmi mevcut: input ALTINDA info kutusu — helperText degil.
            const buildPanel = (severity) => {
              const lines = [];
              if (!isMissingAmount && pastMonths != null && pastMonths > 0) {
                lines.push(t(
                  'userRevenueEntries.panel.pastMonths',
                  {
                    defaultValue: 'Bu iş için bu ciro kanalında daha önce {{months}} aylık prim girilmiş.',
                    months: pastMonths,
                  },
                ));
              }
              if (!isMissingAmount && xy) {
                lines.push(t(
                  'userRevenueEntries.panel.totalPeriod',
                  { defaultValue: 'Girilen dönem dahil toplam prim dönemi {{xy}}\'dir.', xy },
                ));
              }
              if (!isMissingAmount && computed.amount != null) {
                const tutarStr = `${Number(computed.amount).toLocaleString('tr-TR')} ₺`;
                lines.push(t(
                  'userRevenueEntries.panel.quarterTotal',
                  {
                    defaultValue: 'Çeyreğe isabet eden toplam ciro: {{tutar}}.',
                    tutar: tutarStr,
                  },
                ));
              }
              if (sureAsimi) {
                lines.push(t(
                  'userRevenueEntries.panel.exceedsTotalPeriod',
                  {
                    defaultValue: 'Girilen {{girilen}} ay, toplam prim döneminden ({{toplam}} ay) fazla. '
                      + 'Toplam süreden daha fazla ay giremezsiniz.',
                    girilen: userAySayisiNum,
                    toplam: computed.y,
                  },
                ));
              }
              if (computed.warning) {
                lines.push(computed.warning);
              }
              return lines.length ? { lines, severity: sureAsimi ? 'error' : severity } : null;
            };

            // X (ay_sayisi) ve Y (toplam_ay) autoFetch ile yansi — AMA
            // form'da zaten bir deger varsa (edit modda kayitli veya
            // kullanici elle yazmis) uzerine yazma. Sadece bos alanlari
            // doldur.
            //
            // Semantik: ay_sayisi CURRENT ONLY (bu ceyrek katkisi —
            // computed.quarterMonths). Cumulative olsaydi jobInfo card'da
            // SUM alindiginda cifte sayilirdi. Her kayit sadece kendi
            // period katkisini tasir; toplam pay = SUM(ay_sayisi).
            const extraFields = {};
            const currentAy = formData?.ay_sayisi;
            const currentToplamAy = formData?.toplam_ay;
            const isEmpty = (v) => v == null || v === '' || Number(v) === 0;
            // x'i sistem yalnizca OTOMATIK modda doldurur; manuel modda
            // kullanicinin girecegi alani kendi tahminiyle doldurmak, onun
            // yerine karar vermek olurdu.
            if (otomatikTutar && Number.isFinite(computed.quarterMonths) && isEmpty(currentAy)) {
              extraFields.ay_sayisi = computed.quarterMonths;
            }
            if (Number.isFinite(computed.y) && isEmpty(currentToplamAy)) {
              extraFields.toplam_ay = computed.y;
            }

            // Fatura bulunamadi (ornegin ONAY_SONRASI cirosunda "onay"
            // pricing_process'li fatura yok). Kullanicinin manuel girmesi
            // gerekiyor — form otomatik olarak "Manuel Doldur" moduna
            // gecsin, "Prim Alinan Aylar" seciminden auto-doldurulan
            // aylar temizlensin. Boylece kullanici sistemin "3 ay hesapladi,
            // eylul secili" gibi yaniltici bir gorunumle karsilasmaz.
            if (computed.reason === REVENUE_CALC_REASONS.MISSING_CONTRACT_AMOUNT
                || (computed.amount == null && computed.warning)) {
              extraFields.fill_mode = 'manual';
              extraFields.secili_aylar = [];
              // ay_sayisi'i ustte sistem doldurmus olabilir — otomatik hesap
              // yanilticidir, temizleyelim. YALNIZCA OTOMATIK MODDA: manuel
              // modda x'i KULLANICI yaziyor ve hesap her tusta yeniden
              // calistigi icin bu satir yazilan degeri aninda siliyordu
              // (faturasi olmayan onay sonrasi TUBITAK isinde x girilemiyordu).
              if (otomatikTutar) extraFields.ay_sayisi = '';
            }

            // TUTAR yalnizca otomatik modda forma yazilir. Manuel modda
            // (ve onay sonrasi TUBITAK'ta, ki orada mod her zaman manuel)
            // kullanici tutari kendisi giriyor — ama `extraFields` yine
            // doniyor, yani payda (y) her durumda doluyor.
            if (computed.warning) {
              const sev = (computed.canSubmit === false || sureAsimi) ? 'error' : 'warning';
              return {
                amount: otomatikTutar ? computed.amount : null,
                helperText: null,
                severity: sev,
                panel: buildPanel(sev),
                extraFields,
              };
            }
            if (computed.amount == null) {
              return {
                amount: null,
                helperText: computed.breakdown || 'Tutar hesaplanamadı.',
                severity: 'warning',
                extraFields,
              };
            }
            return {
              amount: otomatikTutar ? computed.amount : null,
              helperText: null,
              severity: sureAsimi ? 'error' : 'info',
              panel: buildPanel(sureAsimi ? 'error' : 'info'),
              extraFields,
            };
          } catch (e) {
            console.error('[CiroGetir] Lookup hatası', e);
            return { amount: null, helperText: 'Hata: ' + (e?.message || ''), severity: 'error' };
          }
        },
      };
    });

    // ROOT icin "Kisi Sec" alani: baskasi adina ciro girme akisi. Non-ROOT
    // kullanicilar bu alani gormez — hook targetUserId'yi zaten user.id'ye
    // sabitliyor. Edit modda user_id updateStripFields ile _set'ten dusuyor
    // (kayit sahipligini degistiremeyiz), gorunse de zararsizdir; yine de
    // yeni kayit + duzenleme arasi tutarlilik icin daima render ederiz.
    if (isRootUser) {
      const userOptions = (allUsers || [])
        .filter((u) => !u.companyId || u.companyId === user?.companyId)
        .map((u) => ({
          value: u.id,
          label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || `#${u.id}`,
        }));
      const rootUserField = {
        name: 'user_id',
        type: 'select',
        label: 'userRevenueEntries.form.userIdRoot',
        required: true,
        options: userOptions,
        validation: {
          required: true,
          requiredMessage: 'modals.form.validation.required',
        },
      };
      return [rootUserField, ...withTutar];
    }
    return withTutar;
    // userRevenueEntries dep'i KASTEN yok: ref üzerinden okuyoruz. Burada
    // olsaydı her entry güncellemesinde formFields yeniden inşa edilip modal
    // form'u remount edip input'ları sıfırlardı.
  }, [firms, bonusRevenueTypes, user?.companyId, isRootUser, allUsers, t,
    // Otomatik doldur kalan ay hakkini mevcut kayitlardan hesapliyor.
    userRevenueEntries]);

  // Tablo kolonlari merkezi TABLE_COLUMNS.userRevenueEntries'tan — status
  // kolonu (RevenueStatusChip) zaten icerikte. ROOT-only kolonlari filtrele.
  const columns = useMemo(
    () =>
      (TABLE_COLUMNS.userRevenueEntries || []).filter(
        (col) => !col.showIfRoot && !col.hiddenFromTable,
      ),
    [],
  );

  // ROOT-only company_name filtresini ve userRevenueEntries icin gereksiz
  // team filtresini (kullanici sadece kendi kayitlarini gorur) ele.
  // bonus_revenue_type_id (Ciro Turu) filter'inin options'larini bonusRevenueTypes'den enjekte et.
  const filterConfigs = useMemo(() => {
    const typeOptions = (bonusRevenueTypes || []).map((b) => ({
      value: b.id,
      label: b.name || `#${b.id}`,
    }));
    return (FILTER_CONFIGS.userRevenueEntries || [])
      .filter((cfg) => !cfg.showIfRoot && cfg.field !== 'team_id' && cfg.field !== 'user_name')
      .map((cfg) =>
        cfg.field === 'bonus_revenue_type_id'
          ? { ...cfg, options: typeOptions }
          : cfg,
      );
  }, [bonusRevenueTypes]);

  const onSubmit = useCallback(
    async (formData, isEditMode, currentEditData) => {
      // DataTableContainer 3 parametre yolluyor; useDynamicEntity sadece
      // (formData, entityId). isEditMode boolean'ı yanlışlıkla entityId
      // yerine geçince update branchine girip aynı satırı eziyordu.
      // Açıkça çözümle: edit modunda gerçek id'yi geç, değilse null.
      const entityId = isEditMode ? currentEditData?.id ?? null : null;

      // Server'a gitmeden önce y-aşımı kontrolü + ay_sayisi patch.
      // "Tutar Getir"e basmamış (elle tutar yazmış) kullanıcı için son
      // savunma. Kural bulunmazsa (eski kayıtlar) kontrolü atla,
      // blocking değil.
      // İlk olarak Tutar Getir'in son hesabını uygula (fallback). Submit
      // guard kuralı çekebilirse zaten override edecek.
      let patchedFormData = formData;
      const lastFetch = lastFetchMonthsRef.current;
      // Manuel modda x kullanicidan gelir; bos birakilmissa sistemin
      // tahminiyle doldurulmaz.
      const manuelMod = resolveFillMode(formData, findSelectedJob(firms, formData)) === 'manual';
      if (
        !manuelMod
        && (formData.ay_sayisi == null || formData.ay_sayisi === '')
        && lastFetch.months != null
      ) {
        const fetchKey = [
          formData.firm_id,
          formData.job_id || formData.contract_id || '',
          formData.ciro_kanali,
          formData.yil,
          formData.ceyrek,
        ].join('|');
        if (lastFetch.key === fetchKey) {
          patchedFormData = { ...formData, ay_sayisi: lastFetch.months };
        }
      }

      // Paket 2 — Submit'i ENGELLEYEN 4 kırmızı uyarı. Sıralama önemli:
      // önce kural-bağımsız blocker'ları kontrol et (toast göster, form
      // korunur), sonra computeQuarterAmount sonucu varsa period_over.
      // İlk tetiklenen blocker'ı toast olarak göster (kullanıcı bir kez
      // bir tane düzeltebilsin diye), submit iptal — form silinmez.
      const channelEnum = formData?.ciro_kanali;
      const firmsList = firms || [];
      const selectedFirm = firmsList.find(
        (fm) => fm.id === parseInt(formData.firm_id) || fm.id === formData.firm_id,
      );
      let contract = null;
      let job = null;
      if (channelEnum === 'SATIS_CIROSU') {
        contract = selectedFirm?.contracts?.find(
          (c) => c.id === parseInt(formData.contract_id) || c.id === formData.contract_id,
        );
        if (contract?.id) {
          job = selectedFirm?.jobs?.find((j) => j.contract_id === contract.id) || null;
        }
      } else if (formData?.job_id) {
        job = selectedFirm?.jobs?.find((j) => j.id === formData.job_id) || null;
        if (job?.contract_id) {
          contract = selectedFirm?.contracts?.find((c) => c.id === job.contract_id) || null;
        }
      }

      const showBlocker = (key) => {
        const msg = t(`userRevenueEntries.blocking.${key}`, BLOCKING_DEFAULTS[key]);
        showNotification(msg, 'error');
      };

      // computed bağımsız blocker'ları erken yakala. Duplicate kontrolü
      // icin mevcut entries + edit modunda kendi id'sini haric tut.
      const preBlockers = evaluateBlockingWarnings({
        entry: formData,
        job,
        firm: selectedFirm,
        computed: null,
        existingEntries: userRevenueEntriesRef.current,
        currentEntryId: entityId,
      });
      if (preBlockers.length > 0) {
        showBlocker(preBlockers[0]);
        return null;
      }

      try {
        if (channelEnum && formData?.firm_id && formData?.yil && formData?.ceyrek) {
          const CHANNEL_MAP = {
            SATIS_CIROSU: 'SATIS',
            PROJE_YAZIM_CIROSU: 'YAZIM',
            ONAY_SONRASI_CIROSU: 'ONAY_SONRASI',
          };
          const revenueChannel = CHANNEL_MAP[channelEnum];
          const pricingMethodId = contract?.pricing_method_id;
          const companyId = user?.companyId;
          const jobTypeId = job?.job_type_id;
          if (pricingMethodId && companyId && (channelEnum === 'SATIS_CIROSU' || jobTypeId)) {
            const useSalesNullQuery = channelEnum === 'SATIS_CIROSU' && !jobTypeId;
            const query = useSalesNullQuery
              ? GET_REVENUE_CALCULATION_RULE_MATCH_FOR_SALES_QUERY
              : GET_REVENUE_CALCULATION_RULE_MATCH_QUERY;
            const params = useSalesNullQuery
              ? { companyId, revenueChannel, pricingMethodId }
              : { companyId, jobTypeId, revenueChannel, pricingMethodId };
            const result = await hasuraClient.query(query, params);
            const rule = result?.revenue_calculation_rules?.[0];
            if (rule) {
              let approvalDate = null;
              if (Array.isArray(job?.job_details)) {
                for (const d of job.job_details) {
                  const dd = d?.detail_data;
                  if (dd?.approvalDate) { approvalDate = dd.approvalDate; break; }
                }
              }
              const computed = computeQuarterAmount({
                rule,
                contract,
                job,
                approvalDate,
                yil: formData.yil,
                ceyrek: formData.ceyrek,
                existingEntries: userRevenueEntries || [],
                currentEntryId: entityId,
                selectedMonths: Array.isArray(formData.secili_aylar)
                  ? formData.secili_aylar
                  : null,
                enteredMonths: patchedFormData.ay_sayisi,
                onlyEnteredMonths: manuelMod,
              });
              // Eski window.alert (y-aşımı) kaldırıldı — aynı semantic
              // şimdi period_over blocker'ında. evaluateBlockingWarnings
              // hem QUARTER_AFTER_END hem x>y durumunu yakalar.
              const postBlockers = evaluateBlockingWarnings({
                entry: formData,
                job,
                firm: selectedFirm,
                computed,
                existingEntries: userRevenueEntriesRef.current,
                currentEntryId: entityId,
              });
              if (postBlockers.length > 0) {
                showBlocker(postBlockers[0]);
                return null;
              }
              // Hesaplanan quarterMonths'u formData'ya SADECE kullanici manuel
              // girmediyse yaz. Kullanici formda "Ay Sayisi (Bu Kayit)" alanini
              // 6 yaptiysa (sistem 3 hesaplasa bile) onun degeri KORUNUR.
              // Payda (y) sabittir, pay (x) kullanici tarafindan set edilebilir.
              const userAySayisi = patchedFormData.ay_sayisi;
              const isUserProvided = userAySayisi !== null
                && userAySayisi !== undefined
                && userAySayisi !== ''
                && Number.isFinite(Number(userAySayisi));
              if (
                !manuelMod
                && !isUserProvided
                && computed
                && Number.isFinite(computed.quarterMonths)
              ) {
                patchedFormData = { ...patchedFormData, ay_sayisi: computed.quarterMonths };
              }
              // y-limit sertlestirme: kullanici override etse bile
              // finalX = pastMonths + kullanicinin current'i > y ise reddet.
              // pastMonths compute icinde hesaplanip donduruluyor.
              if (computed && Number.isFinite(computed.y) && computed.y > 0) {
                const pastFromCompute = Number.isFinite(computed.pastMonths) ? computed.pastMonths : 0;
                const currentAy = Number(patchedFormData.ay_sayisi);
                if (Number.isFinite(currentAy) && currentAy > 0) {
                  const totalX = pastFromCompute + currentAy;
                  if (totalX > computed.y) {
                    showNotification(
                      t(
                        'userRevenueEntries.blocking.exceeds_total',
                        {
                          defaultValue: 'Toplam ay sayısı ({{total}}/{{y}}) toplam prim ay sayısını aşıyor. Onaylanmış geçmiş {{past}} ay, bu kayıtta girdiğiniz {{cur}} ay.',
                          total: totalX,
                          y: computed.y,
                          past: pastFromCompute,
                          cur: currentAy,
                        },
                      ),
                      'error',
                    );
                    return null;
                  }
                }
              }
            }
          }
        }
      } catch (e) {
        console.warn('[CiroSubmit] doğrulama atlandı', e);
      }

      // KURALDAN BAGIMSIZ SON KONTROL — toplam ay asimi.
      //
      // Yukaridaki y-limit kontrolu `if (rule)` blogunun ICINDE ve dis
      // try/catch hatayi yutup "dogrulama atlandi" diyor. Yani kural
      // cozulemeyen bir iste (eski kayit, kurali olmayan is) ya da
      // hesaplama patladiginda hicbir sinir kalmiyor ve kullanici elle
      // istedigi sayiyi kaydedebiliyor. Ibrahim'in ekranda gosterdigi
      // "11/23" kaydinin olusma yolu bu: "Bu nasil izin vermis ki?"
      //
      // Burasi kurala BAKMAZ. Toplam ay (y) ya formda yazili ya da ayni
      // is+kanaldaki onceki kayitlarda duruyor; gecmis + bu kayit o
      // toplami asiyorsa kayit durur.
      //
      // Ibrahim: "Personel oraya 12 de yazabilir ama senin hata vermen
      // lazim, toplam sureden daha fazla giremezsin diye. Izin ver."
      // Yani alana YAZILABILIYOR, kaydetme durduruluyor.
      try {
        const buKayit = Number(patchedFormData.ay_sayisi);
        if (Number.isFinite(buKayit) && buKayit > 0) {
          const ayniIs = (userRevenueEntriesRef.current || []).filter((e2) => {
            if (e2?.status === 'REJECTED') return false;
            if (entityId != null && e2?.id === entityId) return false;
            if (patchedFormData.job_id != null) {
              if (e2?.job_id !== patchedFormData.job_id) return false;
            } else if (patchedFormData.contract_id != null) {
              if (e2?.contract_id !== patchedFormData.contract_id) return false;
            } else return false;
            return e2?.ciro_kanali === patchedFormData.ciro_kanali;
          });
          // Toplam sure: formdaki deger, yoksa gecmis kayitlardaki en buyugu.
          const formToplam = Number(patchedFormData.toplam_ay);
          const gecmisToplam = ayniIs.length
            ? Math.max(...ayniIs.map((e2) => Number(e2.toplam_ay) || 0))
            : 0;
          const toplamAy = Number.isFinite(formToplam) && formToplam > 0
            ? formToplam
            : gecmisToplam;
          if (toplamAy > 0) {
            const gecmisGirilen = ayniIs.reduce(
              (acc, e2) => acc + (Number(e2.ay_sayisi) || 0), 0,
            );
            const yeniToplam = gecmisGirilen + buKayit;
            if (yeniToplam > toplamAy) {
              showNotification(
                t(
                  'userRevenueEntries.blocking.exceeds_total',
                  {
                    defaultValue: 'Toplam ay sayısı ({{total}}/{{y}}) toplam prim ay sayısını aşıyor. Onaylanmış geçmiş {{past}} ay, bu kayıtta girdiğiniz {{cur}} ay.',
                    total: yeniToplam,
                    y: toplamAy,
                    past: gecmisGirilen,
                    cur: buKayit,
                  },
                ),
                'error',
              );
              return null;
            }
          }
        }
      } catch (e) {
        // Bu kontrol SESSIZCE atlanmamali — yukaridaki blogun sorunu buydu.
        console.error('[CiroSubmit] toplam ay kontrolu calismadi', e);
      }

      // eslint-disable-next-line no-console
      console.log('[Submit-BEFORE]', {
        secili_aylar_raw: patchedFormData.secili_aylar,
        ay_sayisi_raw: patchedFormData.ay_sayisi,
        allKeys: Object.keys(patchedFormData),
      });
      // secili_aylar'i normalize et (uniq + valid range).
      // ay_sayisi: kullanici ELLE girdiyse (>0 sayi) ona SAYGI goster;
      // yoksa secili_aylar.length default'una dus. Boylece "3 ay sectim
      // ama x/y kolonunda 11 gorunsun" case'i (kullanici ay_sayisi=11
      // yazar, secili_aylar 3 kalir) mumkun olur.
      if (Array.isArray(patchedFormData.secili_aylar) && patchedFormData.secili_aylar.length > 0) {
        const uniq = [...new Set(
          patchedFormData.secili_aylar.map((n) => parseInt(n, 10)).filter((n) => n >= 1 && n <= 12),
        )];
        const userAySayisi = Number(patchedFormData.ay_sayisi);
        const keepUserAySayisi = Number.isFinite(userAySayisi) && userAySayisi > 0;
        patchedFormData = {
          ...patchedFormData,
          secili_aylar: uniq,
          ay_sayisi: keepUserAySayisi ? userAySayisi : uniq.length,
        };
      }
      // eslint-disable-next-line no-console
      console.log('[Submit-AFTER]', {
        secili_aylar_final: patchedFormData.secili_aylar,
        ay_sayisi_final: patchedFormData.ay_sayisi,
        entityId,
      });
      // UI-only field'lari cikar (ROOT'un formul kartinda revize ettigi
      // gecmis toplami _past_override olarak tutuluyor; DB kolonu yok).
      // eslint-disable-next-line no-unused-vars
      const { _past_override, ...cleanFormData } = patchedFormData;
      return handleFormSubmit(cleanFormData, entityId);
    },
    [firms, userRevenueEntries, user?.companyId, handleFormSubmit, showNotification, t],
  );

  // Iş / sözleşme + kanal secildiginde form içinde altta gösterilecek info kartı.
  // Existing entries üzerinden pastMonths + son giriş bilgisi hesaplar.
  const renderFieldExtras = useCallback((fieldName, formData, currentUser, onFormChange) => {
    // AYLIK TUTAR NOTU — Ibrahim (kayit 26, 13:56-14:48): CT2 (aylikli
    // proje yazim) ve CT3 (aylikli satis) girilirken cocuklar tutari 2 ile
    // carpip giriyor, sistem de 2 ile carpiyor, 4 kati oluyor. "Lutfen
    // bir aylik tutari giriniz" notu, tutar alaninin altinda kirmizi.
    if (fieldName === 'tutar') {
      const tur = (bonusRevenueTypes || []).find(
        (b) => b.id === Number(formData?.bonus_revenue_type_id) || b.id === formData?.bonus_revenue_type_id,
      );
      const kod = String(tur?.code || '').trim().toUpperCase();
      if (!AYLIK_TUTAR_KODLARI.has(kod)) return null;
      return (
        <Typography sx={{ mt: 0.75, fontSize: 12.5, fontWeight: 700, color: 'error.main' }}>
          {t('userRevenueEntries.form.monthlyAmountOnly', { kod })}
        </Typography>
      );
    }
    // ay_sayisi altina hesap formulu goster (X = past + current)
    // Formul kartini artik iş kartinin altinda gosteriyoruz (her role, ONAY_SONRASI'da).
    // Boylece non-ROOT non-manual modda ay_sayisi gizli olsa bile hesap gozukur.
    if (fieldName === 'ay_sayisi') return null;

    if (fieldName !== 'job_id' && fieldName !== 'contract_id') return null;
    const channelEnum = formData?.ciro_kanali;
    if (!channelEnum) return null;
    const jobId = formData?.job_id;
    const contractId = formData?.contract_id;
    if (channelEnum === 'SATIS_CIROSU' && !contractId) return null;
    if (channelEnum !== 'SATIS_CIROSU' && !jobId) return null;

    // KAPANMIS IS UYARISI — girerken gorunsun (Ibrahim, kayit 26, 12:14:
    // "Girerken uyariyi cikartsin"). ENGEL DEGIL: "Girmesini sen engelleme,
    // benim gorup onu engellemem lazim." Ayni uyari onay ekraninda da var.
    const secilenFirma = (firms || []).find(
      (fm) => fm.id === Number(formData?.firm_id) || fm.id === formData?.firm_id,
    );
    const kapanis = entryClosure(
      channelEnum === 'SATIS_CIROSU' ? null : (secilenFirma?.jobs || []).find((j) => j.id === jobId),
      channelEnum === 'SATIS_CIROSU'
        ? (secilenFirma?.jobs || []).filter((j) => j.contract_id === contractId)
        : null,
    );
    const kapanisUyarisi = kapanis ? (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {warningKeyText(closureWarningKey(kapanis), t)}
        {' '}{t('userRevenueEntries.warnings.is_kapandi.notBlocking')}
      </Alert>
    ) : null;

    // Bu is+kanal icin gecmis girisleri filtrele (REJECTED hariç).
    const list = (userRevenueEntries || []).filter((e) => {
      if (e.status === 'REJECTED') return false;
      if (e.ciro_kanali !== channelEnum) return false;
      if (channelEnum === 'SATIS_CIROSU') {
        return e.contract_id === contractId;
      }
      return e.job_id === jobId;
    });

    // ONAY_SONRASI icin sure hesap formulu — tum roller gorur. ROOT
    // "Gecmiste Girilen Sure"yi tiklayip revize edebilir; diger roller
    // sadece okur.
    //
    // Grouping: is + kanal seviyesinde ceyrek ayirt etmeksizin. jobInfo
    // card ile ayni: bu is icin bu kanaldaki tum onceki kayitlarin
    // ay_sayisi toplami "gecmis"tir. Kendi kaydini edit modda haric.
    let formulaCard = null;
    if (channelEnum === 'ONAY_SONRASI_CIROSU' && jobId) {
      // Semantik: ay_sayisi CURRENT ONLY (bu kaydin kendi period katkisi).
      //   past    = list.reduce(ay_sayisi kendini haric) — bu iş+kanal
      //             icin diger kayitlarin katkilari toplami (jobInfo card
      //             ile tutarli).
      //   current = ay_sayisi (kayitin kendi katkisi)
      //   total   = past + current (kayit ile birlikte toplam)
      //
      // Canli _past_override (Root badge tikladiginda): past = override.
      const currentEntryId = formData?.id;
      const pastOverride = formData?._past_override;
      const hasLiveOverride = pastOverride != null && pastOverride !== ''
        && Number.isFinite(Number(pastOverride));
      const currentInput = Number(formData?.ay_sayisi) || 0;

      // Persistent override (ROOT revize): varsa OverrideMonths + o
      // override'dan SONRA girilen kayitlarin ay_sayisi toplami.
      const overrideKey = `${jobId}|${channelEnum}`;
      const persistedOverride = pastOverrides?.[overrideKey] || null;

      // Bu row'un chronological rank'i (yil*10 + ceyrek). Past hesabinda
      // sadece bu row'dan ONCEKI (daha erken yil/ceyrek) kayitlar dahil
      // edilir — Q3 girildi diye Q2 formunda past bozulmasin.
      const rowYil = Number(formData?.yil) || 0;
      const rowCeyrek = Number(formData?.ceyrek) || 0;
      const rowRank = rowYil * 10 + rowCeyrek;

      let past;
      if (hasLiveOverride) {
        past = Number(pastOverride);
      } else if (persistedOverride) {
        // override + (bu row'dan ONCE olan VE o override tarihinden SONRA
        // girilen kayitlarin toplami). Kendini haric.
        const afterOverride = (userRevenueEntries || []).filter((e) => {
          if (e.status === 'REJECTED') return false;
          if (e.ciro_kanali !== channelEnum) return false;
          if (e.job_id !== jobId) return false;
          if (currentEntryId != null && e.id === currentEntryId) return false;
          const eRank = (Number(e.yil) || 0) * 10 + (Number(e.ceyrek) || 0);
          if (eRank >= rowRank) return false; // sadece bu row'dan onceki period
          return (e.created_at || '') > (persistedOverride.created_at || '');
        });
        past = Number(persistedOverride.override_months || 0)
          + afterOverride.reduce((acc, e) => acc + (Number(e.ay_sayisi) || 0), 0);
      } else {
        // Kronolojik siralama: past = bu kayittan ONCE (daha erken yil/ceyrek)
        // olan kayitlarin ay_sayisi toplami. Q1 icin past=0 (sonraki Q2/Q3
        // sayilmaz). Q2 icin Q1 past'ta.
        past = (userRevenueEntries || [])
          .filter((e) => {
            if (e.status === 'REJECTED') return false;
            if (e.ciro_kanali !== channelEnum) return false;
            if (e.job_id !== jobId) return false;
            if (currentEntryId != null && e.id === currentEntryId) return false;
            const eRank = (Number(e.yil) || 0) * 10 + (Number(e.ceyrek) || 0);
            return eRank < rowRank;
          })
          .reduce((acc, e) => acc + (Number(e.ay_sayisi) || 0), 0);
      }
      const totalX = past + currentInput;
      const isManual = resolveFillMode(formData, findSelectedJob(firms, formData)) === 'manual';
      const y = Number(formData?.toplam_ay) || null;
      const isRoot = currentUser?.role === 'ROOT';
      formulaCard = (
        <Box
          sx={{
            mt: 1, p: 1.5, borderRadius: 2,
            border: '1px dashed',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography sx={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
            textTransform: 'uppercase', color: 'primary.dark', mb: 0.75,
          }}>
            Süre Hesap Formülü {isManual ? '(Manuel)' : '(Otomatik)'}
            {(hasLiveOverride || persistedOverride) && (
              <Box component="span" sx={{ ml: 1, fontSize: 9, color: 'warning.dark', fontWeight: 600 }}>
                (Geçmiş revize edildi)
              </Box>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormulaBadge
              label="Geçmişte Girilen Süre"
              value={`${past}`}
              color="info"
              editable={isRoot}
              editValue={past}
              onEditSave={(newPast) => {
                if (!onFormChange) return;
                // Root "Gecmis N" dedi. PERSISTENT olarak DB'ye insert et —
                // sonraki formlarda past hesabinda kullanilir.
                //
                // ay_sayisi'a DOKUNMA — semantik CURRENT ONLY: ay_sayisi
                // sadece bu kaydin period katkisi. Root sadece past'i
                // revize ediyor, current degismiyor. Formula: past =
                // _past_override, current = ay_sayisi (sabit), total = past + current.
                onFormChange('_past_override', newPast);
                insertPastOverride({
                  jobId,
                  ciroKanali: channelEnum,
                  overrideMonths: newPast,
                });
              }}
            />
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'text.secondary' }}>+</Typography>
            <FormulaBadge
              label="Şu Anda Girilen Süre"
              value={`${currentInput}`}
              color={isManual ? 'warning' : 'success'}
            />
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'text.secondary' }}>=</Typography>
            <FormulaBadge label="Toplam" value={`${totalX}`} color="primary" bold />
            {y ? (
              <>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'text.secondary', mx: 0.5 }}>/</Typography>
                <FormulaBadge label="Toplam Süre" value={`${y}`} color="secondary" />
              </>
            ) : null}
          </Box>
          {isManual && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Manuel modda toplam değeri yaz; sistem geçmişi çıkararak şu anda girilen süreyi gösterir.
            </Typography>
          )}
        </Box>
      );
    }

    if (list.length === 0) {
      return (
        <>
          {kapanisUyarisi}
          <Box
            sx={{
              mt: 1, p: 2, borderRadius: 2,
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.success.main, 0.25),
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.05),
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 36, height: 36, borderRadius: 1.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.15),
                color: 'success.main', flexShrink: 0,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                textTransform: 'uppercase', color: 'success.dark', mb: 0.25,
              }}>
                {t('userRevenueEntries.jobInfo.title', { defaultValue: 'Bu İş İçin Prim Geçmişi' })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('userRevenueEntries.jobInfo.firstEntry', {
                  defaultValue: 'Bu iş için bu ciro kanalında henüz prim girilmemiş — ilk giriş bu olacak.',
                })}
              </Typography>
            </Box>
          </Box>
          {formulaCard}
        </>
      );
    }

    // jobInfo "Girilen Süre" — persistent override'i de dahil et:
    //   override varsa → override.months + (o tarihten sonra girilen kayitlar)
    //   yoksa → tum kayitlarin ay_sayisi toplami (chronological SUM)
    const overrideKey = `${jobId}|${channelEnum}`;
    const persistedOverrideForInfo = pastOverrides?.[overrideKey] || null;
    let pastMonths;
    if (persistedOverrideForInfo) {
      const overrideCreated = persistedOverrideForInfo.created_at || '';
      const afterOverride = list.filter((e) => (e.created_at || '') > overrideCreated);
      pastMonths = Number(persistedOverrideForInfo.override_months || 0)
        + afterOverride.reduce((acc, e) => acc + (Number(e.ay_sayisi) || 0), 0);
    } else {
      pastMonths = list.reduce((acc, e) => acc + (Number(e.ay_sayisi) || 0), 0);
    }
    const totalAmount = list.reduce((acc, e) => acc + (Number(e.tutar) || 0), 0);
    const last = [...list].sort((a, b) => (b?.created_at || '').localeCompare(a?.created_at || ''))[0];
    const lastYQ = last ? `${last.yil}/Q${last.ceyrek}` : null;
    const totalStr = totalAmount ? `${totalAmount.toLocaleString('tr-TR')} ₺` : null;

    // MUI Chip yerine tamamen pasif (click event'i olmayan) custom badge.
    // Chip'e otomatik eklenen click handler'lari drag-and-drop veya modal
    // scope'larinda garip event bubbling'e yol acabiliyordu.
    const StatBadge = ({ icon, iconBg, iconColor, label, value }) => (
      <Box
        sx={{
          flex: '1 1 140px', minWidth: 140,
          display: 'flex', alignItems: 'center', gap: 1.25,
          p: 1.25, borderRadius: 1.5,
          bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          '&:hover': {
            borderColor: 'primary.light',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          },
        }}
      >
        <Box
          sx={{
            width: 32, height: 32, borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: iconBg, color: iconColor, flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
              textTransform: 'uppercase', color: 'text.secondary',
              lineHeight: 1.2,
            }}
            noWrap
          >
            {label}
          </Typography>
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
            noWrap
          >
            {value}
          </Typography>
        </Box>
      </Box>
    );

    return (
      <>
        {kapanisUyarisi}
        <Box
          sx={{
            mt: 1, p: 2, borderRadius: 2,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.03),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
            <HistoryOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
            <Typography sx={{
              fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
              textTransform: 'uppercase', color: 'info.dark',
            }}>
              {t('userRevenueEntries.jobInfo.title', { defaultValue: 'Bu İş İçin Prim Geçmişi' })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <StatBadge
              icon={<WorkOutlineIcon sx={{ fontSize: 18 }} />}
              iconBg={(theme) => alpha(theme.palette.info.main, 0.12)}
              iconColor="info.main"
              label={t('userRevenueEntries.jobInfo.pastMonthsLabel', { defaultValue: 'Girilen Süre' })}
              value={t('userRevenueEntries.jobInfo.pastMonthsValue', { defaultValue: '{{n}} ay', n: pastMonths })}
            />
            {totalStr && (
              <StatBadge
                icon={<PaymentsOutlinedIcon sx={{ fontSize: 18 }} />}
                iconBg={(theme) => alpha(theme.palette.success.main, 0.12)}
                iconColor="success.main"
                label={t('userRevenueEntries.jobInfo.totalLabel', { defaultValue: 'Toplam Ciro' })}
                value={totalStr}
              />
            )}
            {lastYQ && (
              <StatBadge
                icon={<EventAvailableOutlinedIcon sx={{ fontSize: 18 }} />}
                iconBg={(theme) => alpha(theme.palette.warning.main, 0.12)}
                iconColor="warning.main"
                label={t('userRevenueEntries.jobInfo.lastEntryLabel', { defaultValue: 'Son Giriş' })}
                value={lastYQ}
              />
            )}
            <StatBadge
              icon={<ArticleOutlinedIcon sx={{ fontSize: 18 }} />}
              iconBg={(theme) => alpha(theme.palette.text.primary, 0.08)}
              iconColor="text.secondary"
              label={t('userRevenueEntries.jobInfo.entryCountLabel', { defaultValue: 'Kayıt' })}
              value={t('userRevenueEntries.jobInfo.entryCountValue', { defaultValue: '{{n}} adet', n: list.length })}
            />
          </Box>
        </Box>
        {formulaCard}
      </>
    );
  }, [userRevenueEntries, firms, bonusRevenueTypes, t]);

  return (
    <>
      <PageContainer currentPage={t('navigation.ciroEkle', 'Ciro Ekle')}>
        <DataTableContainer
          title={t('navigation.ciroEkle', 'Ciro Ekle')}
          entityType="userRevenueEntries"
          data={enrichedData}
          loading={isLoading}
          columns={columns}
          filterConfigs={filterConfigs}
          searchFields={SEARCH_CONFIGS.userRevenueEntries?.fields || []}
          searchPlaceholder={t(
            'userRevenueEntries.search.placeholder',
            'Ciro kaydı ara...',
          )}
          formFields={formFields}
          onFormSubmit={onSubmit}
          transformEditData={(row) => {
            if (!row) return {};
            // ONAY_SONRASI multiselect'in edit modalında dolu gelmesi için
            // null → [] normalize. Ayrica edit modunda `fill_mode` field'i
            // yeniden hesaplanir — kullanici Manuel modda kaydettiyse edit'te
            // de Manuel acilsin. `secili_aylar` daha guvenilir bir sinyal:
            // Manuel modda form Manuel'e gecince secili_aylar gizlenir ve
            // submit'te null yazilir; Auto modda kullanici secim yapar ve
            // dizi kaydedilir.
            const seciliAylar = Array.isArray(row.secili_aylar) ? row.secili_aylar : [];
            const isAuto = seciliAylar.length > 0;
            return {
              ...row,
              secili_aylar: seciliAylar,
              fill_mode: isAuto ? 'auto' : 'manual',
            };
          }}
          onDelete={async (row) => {
            const res = await deleteUserRevenueEntries(row?.id);
            // Kayit silinince ilgili iş+kanal icin TUM Root revize (past
            // override) kayitlarini da temizle. Aksi halde silinen kaydin
            // "kalintisi" olarak override past hesabinda etkili kalir.
            if (row?.job_id && row?.ciro_kanali) {
              try {
                await hasuraClient.mutate(DELETE_PAST_OVERRIDES_FOR_JOB_CHANNEL, {
                  jobId: row.job_id,
                  ciroKanali: row.ciro_kanali,
                });
                setPastOverrides((prev) => {
                  const copy = { ...prev };
                  delete copy[`${row.job_id}|${row.ciro_kanali}`];
                  return copy;
                });
              } catch (_e) { /* override yoksa sorun degil */ }
            }
            return res;
          }}
          rowActions={rowActions}
          onRowAction={handleRowAction}
          addButtonText={t('userRevenueEntries.addButton')}
          addButtonIcon={<AddIcon />}
          renderFieldExtras={renderFieldExtras}
        />
      </PageContainer>
      <CiroTalepDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailEntry(null);
        }}
        entry={detailEntry}
      />
    </>
  );
};

export default CiroEklePage;
